import { ChangeDetectorRef, Component, NgZone, OnInit, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IonIcon,
  IonSpinner
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  eyeOutline,
  eyeOffOutline,
  lockClosedOutline,
  checkmarkCircle,
  closeCircle,
  checkmarkCircleOutline
} from 'ionicons/icons';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AuthService, PasswordRequirements } from '../../../services/auth.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    FormsModule,
    IonIcon,
    IonSpinner,
    TranslatePipe
],
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit {
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  token = '';
  password = '';
  confirmPassword = '';
  showPassword = false;
  showConfirmPassword = false;
  isLoading = false;
  isSubmitted = false;
  errorMessage = '';

  passwordRequirements: PasswordRequirements = {
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false
  };

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    addIcons({
      eyeOutline,
      eyeOffOutline,
      lockClosedOutline,
      checkmarkCircle,
      closeCircle,
      checkmarkCircleOutline
    });
  }

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    if (!this.token) {
      this.errorMessage = 'RESET_TOKEN_MISSING';
    }
  }

  onPasswordChange(): void {
    this.passwordRequirements = this.authService.validatePassword(this.password);
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onSubmit(): void {
    if (!this.isFormValid()) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.authService.resetPassword({
      token: this.token,
      password: this.password
    }).subscribe({
      next: (response) => {
        this.runInAngular(() => {
          this.isLoading = false;
          if (response.success) {
            this.isSubmitted = true;
          }
        });
      },
      error: (error) => {
        this.runInAngular(() => {
          this.isLoading = false;
          this.errorMessage = this.resolveErrorKey(error, 'RESET_PASSWORD_ERROR');
        });
      }
    });
  }

  isFormValid(): boolean {
    return (
      this.token.length > 0 &&
      this.authService.isPasswordValid(this.password) &&
      this.password === this.confirmPassword
    );
  }

  passwordsMatch(): boolean {
    return this.confirmPassword.length === 0 || this.password === this.confirmPassword;
  }

  get allRequirementsMet(): boolean {
    return Object.values(this.passwordRequirements).every(Boolean);
  }

  goToLogin(): void {
    this.router.navigate(['/login']);
  }

  private resolveErrorKey(error: any, fallbackKey: string): string {
    const message = error?.error?.message || error?.message;
    if (typeof message === 'string' && /^[A-Z0-9_]+$/.test(message)) {
      return message;
    }
    return fallbackKey;
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
